import { AIAssistancePlugin } from 'Plugins/AIAssistance/AIAssistance.plugin'
import { GrammarPlugin } from 'Plugins/Grammer/Grammar.plugin'
import { PlagiarismPlugin } from 'Plugins/Plagiarism/Plagiarism.plugin'
import { ReadabilityPlugin } from 'Plugins/Readability/Readability.plugin'
import { SentimentPlugin } from 'Plugins/Sentiment/Sentiment.plugin'
import { SoftRulesPlugin } from 'Plugins/SoftRules/SoftRules.plugin'
import { Studio } from 'Core/Studio'
import { MathTypePlugin } from 'Plugins/MathType/MathType.plugin.ts'
import { convertToSnakeCase } from 'Utils/common.utils'
import { SummarisationQualityPlugin } from 'Plugins/SummarisationQuality/SummarisationQuality.plugin.ts'
import { LiveFeedbackPlugin } from 'Plugins/LiveFeedback/LiveFeedback.plugin'
import { AIDetectorPlugin } from 'Plugins/AIDetector/AIDetector.plugin'
import { DisabledPluginsMap } from 'Constants/tasks.constants'
import { ConnectorEventType, PluginBlockCoordinator } from './PluginBlockCoordinator'
import { PluginConnector } from './PluginConnector'
import { IPlugin } from './BasePlugin'
import { CustomValidationPlugin } from 'Plugins/CustomValidation/CustomValidation.plugin'

export type PluginsConfigType = {
    plugins: PluginConfigType[]
}

export type PluginConfigType<T = unknown> = {
    type: string
    id: string
    config: T
    usageStats?: number
}

export type SupportedPluginsKeyType = keyof typeof SupportedPlugins

export const SupportedPlugins = {
    'AI Assistance': AIAssistancePlugin,
    'AI Detection': AIDetectorPlugin,
    Readability: ReadabilityPlugin,
    'Grammar Check': GrammarPlugin,
    Plagiarism: PlagiarismPlugin,
    'Sentiment Analysis': SentimentPlugin,
    'Soft Rules': SoftRulesPlugin,
    'Math Type': MathTypePlugin,
    'Summarisation Quality': SummarisationQualityPlugin,
    'Live Feedback': LiveFeedbackPlugin,
    'Custom Validation': CustomValidationPlugin,
} satisfies {
    [pluginId: string]: new (
        config: PluginConfigType<never>,
        pluginConnectors: PluginConnector[]
    ) => IPlugin
}

export class PluginsManager {
    private installedPlugins: IPlugin[] = []

    constructor(
        private studio: Studio,
        private pluginBlockCoordinator: PluginBlockCoordinator
    ) {
        this.pluginBlockCoordinator.subscribe(ConnectorEventType.CONNECTOR_ADDED, data => {
            const plugin = this.installedPlugins.find(
                plugin => plugin.id === data.addedConnector.pluginId
            )
            if (plugin) {
                plugin.pluginConnectors = this.pluginBlockCoordinator.getPluginConnectors(plugin.id)
            }
        })
        this.pluginBlockCoordinator.subscribe(ConnectorEventType.CONNECTOR_REMOVED, data => {
            const plugin = this.installedPlugins.find(
                plugin => plugin.id === data.removedConnector.pluginId
            )
            if (plugin) {
                plugin.pluginConnectors = this.pluginBlockCoordinator.getPluginConnectors(plugin.id)
            }
        })
    }

    public getInstalledPlugins(): IPlugin[] {
        return this.installedPlugins
    }

    public initializePlugins(config: PluginsConfigType) {
        // fetch and install all plugins
        for (const pluginConfig of config.plugins) {
            this.installPlugin(pluginConfig)
        }
    }

    public getPluginAnalyticsData(pluginKey: SupportedPluginsKeyType): Record<string, unknown> {
        const plugin = this.installedPlugins.find(
            plugin => plugin instanceof SupportedPlugins[pluginKey]
        )

        return (
            plugin?.getAnalyticsData?.() ?? {
                [convertToSnakeCase(pluginKey) + '_used']: `${pluginKey} not enabled`,
            }
        )
    }

    installPlugin(config: PluginConfigType) {
        const Plugin = SupportedPlugins[config.type as SupportedPluginsKeyType] as new (
            config: PluginConfigType,
            pluginConnectors: PluginConnector[]
        ) => IPlugin

        if (!Plugin) {
            console.error('unsupported plugin type: ', config.type)
            return
        }

        /*
         * Some plugins won't be natively supported like the Plagiarism checker
         * for the custom use case so we don't initialize them here.
         * But we still need to add it to Studio object's installed plugins list
         * so we know that the plugin has been enabled from the console.
         */

        const pluginConnectors = this.pluginBlockCoordinator.getPluginConnectors(config.type)
        const installedPlugin = new Plugin(config, pluginConnectors)
        this.installedPlugins.push(installedPlugin)

        if (
            (
                DisabledPluginsMap[this.studio.studioConfig.taskType] as string[] | undefined
            )?.includes(config.type)
        ) {
            console.log(`Plugin: ${config.type} is disabled for this task type`)
            return
        }

        installedPlugin.initialize(this.studio)
    }

    handleSubmissionViolation() {
        const errors = this.studio.inputManager.getViolation().error
        const installedPlugins = this.getInstalledPlugins()
        installedPlugins.forEach(plugin => {
            plugin.highlightViolation(errors)
        })
    }

    destroy() {
        this.installedPlugins.forEach(plugin => {
            plugin.destroy()
        })
    }
}
